import { z } from 'zod'

/**
 * A user's access class. Mirrors the `Role` enum in the Prisma schema — the two
 * must stay in sync. Scoping: SUPER_ADMIN is platform-wide (no tenant);
 * ORGANIZATION_ADMIN spans their whole tenant (incl. managing its people);
 * VENUE_ADMIN is limited to the venues they're assigned to manage; CLIENT sees
 * only their own events. VENDOR sits outside any tenant and manages a single
 * vendor (invited by a SUPER_ADMIN).
 */
export const ROLES = ['SUPER_ADMIN', 'ORGANIZATION_ADMIN', 'VENUE_ADMIN', 'CLIENT', 'VENDOR'] as const

export const roleSchema = z.enum(ROLES)

export type Role = z.infer<typeof roleSchema>

/**
 * A person who belongs to a tenant: organization admin, venue admin, or client.
 *
 * Not a platform super admin, and not a vendor — vendors sit outside any tenant.
 * This is also every role an organization admin may assign.
 */
export const TENANT_MEMBER_ROLES = ['ORGANIZATION_ADMIN', 'VENUE_ADMIN', 'CLIENT'] as const

export type TenantMemberRole = (typeof TENANT_MEMBER_ROLES)[number]

export function isTenantMemberRole(role: Role): role is TenantMemberRole {
  return TENANT_MEMBER_ROLES.some((member) => member === role)
}

/**
 * A company's own staff. Not a client, not a vendor, and not a platform super
 * admin — support mode is a session, not one of these roles.
 */
export const STAFF_ROLES = ['ORGANIZATION_ADMIN', 'VENUE_ADMIN'] as const satisfies readonly Role[]

export type StaffRoleName = (typeof STAFF_ROLES)[number]

export function isStaffRole(role: Role): role is StaffRoleName {
  return STAFF_ROLES.some((staff) => staff === role)
}
